import React, { FC, ReactElement, useCallback, useEffect, useRef, useState } from 'react'
import { styled } from '~/app/components'
import { notEmpty } from '~/app/utils'
import ReactDOMServer from 'react-dom/server'
import useScriptLoader from '~/app/utils/useScriptLoader'
import conf from '~/app/conf'

/**
 * NaverMap component
 * author: soojin.oh
 */

// type definitions
type Props = {
  lat?: number
  lng?: number
  zoom?: number
  markers?: Array<ReactElement<{ lat: number; lng: number }>>
  id?: string
}

// styled components
const Map = styled.div`
  width: 100%;
  height: 100%;
`

// NaverMap component
const NaverMap: FC<Props> = function ({ lat, lng, zoom = 17, markers, id }) {
  // API Key
  const scriptUrl = `https://oapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${conf.naver.maps.key}`
  const mapRef = useRef<HTMLDivElement>(null)
  const [naverMap, setNaverMap] = useState<naver.maps.Map | null>(null)
  const onLoad = useCallback(() => {
    if (mapRef.current !== null && lat && lng) {
      const naverMap = new naver.maps.Map(mapRef.current, {
        center: new naver.maps.LatLng(lat, lng),
        zoom,
      })
      setNaverMap(naverMap)
    }
  }, [lat, lng])
  useScriptLoader(scriptUrl, onLoad, id ?? 'naver-map')

  useEffect(() => {
    const naverMarkers = markers
      ?.map(marker => {
        if (naverMap !== null) {
          const { lat, lng } = marker.props
          return lat && lng
            ? new naver.maps.Marker({
                icon: {
                  content: ReactDOMServer.renderToString(marker),
                  size: new naver.maps.Size(44, 44),
                  anchor: new naver.maps.Point(22, 22),
                },
                map: naverMap,
                position: { lat, lng },
              })
            : null
        }
      })
      .filter<naver.maps.Marker>(notEmpty)
    naverMarkers?.forEach(marker => marker.setMap(naverMap))
    return () => void naverMarkers?.forEach(marker => marker.setMap(null))
  }, [markers, naverMap])

  return <Map ref={mapRef} />
}

export default NaverMap
